import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { Modal } from 'atoms';
import { PopupContainer } from 'molecules';
import { useContributionMutations } from 'core/hooks';

import * as L from '../StudentDetail.styled';
import { formatMoneyCurrency } from '../../_detailShared/format';
import { IAgreement, IContribution } from './types';

interface IMarkPaidManuallyInlinePopupProps {
  contribution: IContribution;
  agreement: IAgreement;
  studentId: string;
  onClose: () => void;
  onSaved?: () => void;
}

/**
 * Spec 0004: «Отметить оплаченным вручную» — the manual escape hatch for
 * cash / undocumented transfers. Date defaults to today, amount defaults to
 * the remaining debt. Submits `markContributionPaidManually`; the backend
 * provisions the hidden `Payment(source='manual_paid')` and the row flips
 * to «оплачен вручную».
 */
export const MarkPaidManuallyInlinePopup = memo(
  ({
    contribution,
    agreement,
    studentId,
    onClose,
    onSaved,
  }: IMarkPaidManuallyInlinePopupProps) => {
    const { t } = useTranslation();
    const { markPaidManually, loading } = useContributionMutations(studentId);

    const currency = agreement.currency || '';
    const defaultAmount =
      contribution.debt ??
      contribution.remainder ??
      contribution.amountForeign ??
      null;

    const [date, setDate] = useState<string>(moment().format('YYYY-MM-DD'));
    const [amount, setAmount] = useState<string>(
      defaultAmount !== null ? String(defaultAmount) : '',
    );

    const onSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();
        const num = parseFloat(amount.replace(',', '.'));
        if (!num || num <= 0 || !date) return;
        const ok = await markPaidManually({
          contributionId: contribution.id,
          date,
          amount: num,
        });
        if (ok) {
          onSaved?.();
          onClose();
        }
      },
      [amount, date, markPaidManually, contribution.id, onSaved, onClose],
    );

    return (
      <Modal>
        <PopupContainer
          width="480px"
          title={t('pages.studentDetail.payments.markManual.title')}
          onClose={onClose}
        >
          <form onSubmit={onSubmit}>
            <L.FormBody>
              <L.InlineNotice
                style={{
                  background: '#f0f9ff',
                  borderColor: '#bae6fd',
                  color: '#075985',
                }}
              >
                {t('pages.studentDetail.payments.markManual.hint', {
                  date: moment(contribution.date).format('DD.MM.YYYY'),
                  amount: formatMoneyCurrency(
                    contribution.amountForeign,
                    currency,
                  ),
                  debt: formatMoneyCurrency(defaultAmount, currency),
                })}
              </L.InlineNotice>

              <L.FormLabel>
                {t('pages.studentDetail.payments.markManual.date')}
                <L.FormInput
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  autoFocus
                />
              </L.FormLabel>

              <L.FormLabel>
                {t('pages.studentDetail.payments.markManual.amount', {
                  currency: currency || '—',
                })}
                <L.FormInput
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </L.FormLabel>

              <L.FormFooter>
                <L.PrimaryActionButton
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                >
                  {t('pages.studentDetail.payments.markManual.cancel')}
                </L.PrimaryActionButton>
                <L.PrimaryActionButton
                  type="submit"
                  tone="primary"
                  disabled={loading || !amount || !date}
                >
                  {loading
                    ? t('pages.studentDetail.payments.markManual.saving')
                    : t('pages.studentDetail.payments.markManual.submit')}
                </L.PrimaryActionButton>
              </L.FormFooter>
            </L.FormBody>
          </form>
        </PopupContainer>
      </Modal>
    );
  },
);
